import { useState, useEffect, useRef, useCallback } from 'react';
import { Reply, Lock, Globe, AtSign, Pencil, Trash2, Check, X, ChevronDown, Send } from 'lucide-react';
import { api } from '../api/client';
import { ConfirmModal } from './ConfirmModal';
import { AutoTextarea, MentionDropdown, InlineReplyForm } from './InlineReplyForm';
import { FileAttachmentPicker, AttachmentList } from './AttachmentWidgets';
import { useT } from '../i18n';
import type { TaskUpdate, TaggableUser, Attachment } from '../types';

interface TaskTimelineProps {
  updates: TaskUpdate[];
  onPost: (content: string, type: 'comment' | 'question' | 'reply', replyToId?: number, taggedUserIds?: number[]) => Promise<number | void>;
  onEdit?: (updateId: number, content: string) => Promise<void>;
  onDelete?: (updateId: number) => Promise<void>;
  currentUserId: number;
  isAdmin?: boolean;
  taskId?: number;
  projectId?: number;
  hideHeader?: boolean;
  onRefresh?: () => void;
  /** false hides the file pickers (project side panel). */
  allowAttachments?: boolean;
  /** Show the thread only: no new comment, reply, edit or delete. */
  readOnly?: boolean;
}

// ── Tree building ──

interface TreeNode {
  update: TaskUpdate;
  children: TreeNode[];
}

function buildTree(updates: TaskUpdate[]): TreeNode[] {
  const map = new Map<number, TreeNode>();
  const roots: TreeNode[] = [];

  for (const u of updates) {
    map.set(u.id, { update: u, children: [] });
  }

  for (const u of updates) {
    const node = map.get(u.id)!;
    if (u.reply_to_id && map.has(u.reply_to_id)) {
      map.get(u.reply_to_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  return roots;
}

// ── Single update node (recursive) ──

function UpdateNode({ node, depth, readOnly, currentUserId, isAdmin, editingId, editContent, replyToId, onStartEdit, onSetEditContent, onCancelEdit, onSaveEdit, onDelete, onDeleteAttachment, onReply, formatTime, renderContent, renderInlineReply }: {
  node: TreeNode;
  depth: number;
  currentUserId: number;
  isAdmin: boolean;
  editingId: number | null;
  editContent: string;
  replyToId: number | null;
  onStartEdit: (u: TaskUpdate) => void;
  onSetEditContent: (v: string) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onDelete: (id: number) => void;
  onDeleteAttachment?: (id: number) => void;
  onReply: (id: number, userName: string) => void;
  /** No reply, edit or delete — the thread is only shown. */
  readOnly?: boolean;
  formatTime: (d: string) => string;
  renderContent: (text: string) => React.ReactNode;
  renderInlineReply: (parentId: number) => React.ReactNode;
}) {
  const { t } = useT();
  const u = node.update;
  const indent = Math.min(depth, 4);

  return (
    <>
      <div
        className={`task-update${depth > 0 ? ' task-update--nested' : ''}`}
        style={indent > 0 ? { paddingLeft: `calc(var(--s16) * ${indent})` } : undefined}
      >
        <div className="task-update-header">
          <span className="task-update-author">{u.user_name}</span>
          <span className="task-update-time">{formatTime(u.created_at)}</span>
          {u.update_type === 'reply' && (
            <span className="task-update-type">{t('taskTimeline.replyBadge')}</span>
          )}
          {u.visibility && (
            <span className={`visibility-badge visibility-badge--${u.visibility}`} title={u.visibility === 'project' ? t('taskTimeline.visibilityProject') : t('taskTimeline.visibilityEveryone')}>
              {u.visibility === 'project' ? <Lock size={10} /> : <Globe size={10} />}
            </span>
          )}
          {!readOnly && (u.user_id === currentUserId || isAdmin) && (
            <span className="task-update-actions">
              {u.user_id === currentUserId && (
                <button className="btn-icon" title={t('common.edit')} onClick={() => onStartEdit(u)}>
                  <Pencil size={12} />
                </button>
              )}
              <button className="btn-icon" title={t('common.delete')} onClick={() => onDelete(u.id)}>
                <Trash2 size={12} />
              </button>
            </span>
          )}
        </div>
        {editingId === u.id ? (
          <div className="task-update-edit">
            <AutoTextarea
              value={editContent}
              onChange={(e) => onSetEditContent(e.target.value)}
              placeholder={t('taskTimeline.editPlaceholder')}
            />
            <div style={{ display: 'flex', gap: 'var(--s4)', marginTop: 'var(--s4)' }}>
              <button className="btn btn-accent btn-sm" onClick={onSaveEdit} disabled={!editContent.trim()}>
                <Check size={12} /> {t('common.save')}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={onCancelEdit}>
                <X size={12} /> {t('common.cancel')}
              </button>
            </div>
          </div>
        ) : (
          <div className="task-update-content">{renderContent(u.content)}</div>
        )}
        {u.attachments && u.attachments.length > 0 && (
          <AttachmentList
            attachments={u.attachments}
            currentUserId={currentUserId}
            isAdmin={isAdmin}
            onDelete={onDeleteAttachment}
          />
        )}
        {!readOnly && editingId !== u.id && replyToId !== u.id && (
          <button
            className="task-update-reply-btn"
            onClick={() => onReply(u.id, u.user_name)}
          >
            <Reply size={11} /> {t('taskTimeline.reply')}
          </button>
        )}
      </div>
      {/* Inline reply form appears directly below this update */}
      {replyToId === u.id && renderInlineReply(u.id)}
      {node.children.map((child) => (
        <UpdateNode
          key={child.update.id}
          node={child}
          depth={depth + 1}
          readOnly={readOnly}
          currentUserId={currentUserId}
          isAdmin={isAdmin}
          editingId={editingId}
          editContent={editContent}
          replyToId={replyToId}
          onStartEdit={onStartEdit}
          onSetEditContent={onSetEditContent}
          onCancelEdit={onCancelEdit}
          onSaveEdit={onSaveEdit}
          onDelete={onDelete}
          onDeleteAttachment={onDeleteAttachment}
          onReply={onReply}
          formatTime={formatTime}
          renderContent={renderContent}
          renderInlineReply={renderInlineReply}
        />
      ))}
    </>
  );
}

// ── Main component ──

const MIN_COLLAPSED_HEIGHT = 700;

export function TaskTimeline({ updates, onPost, onEdit, onDelete, currentUserId, isAdmin = false, taskId, projectId, hideHeader, onRefresh, allowAttachments = true, readOnly = false }: TaskTimelineProps) {
  const { t } = useT();
  const [posting, setPosting] = useState(false);
  const [content, setContent] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [replyTo, setReplyTo] = useState<{ id: number; userName: string } | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const [collapseHeight, setCollapseHeight] = useState(MIN_COLLAPSED_HEIGHT);

  // @mention state for bottom comment form
  const [taggableUsers, setTaggableUsers] = useState<TaggableUser[]>([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionCursorStart, setMentionCursorStart] = useState(0);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [taggedUserIds, setTaggedUserIds] = useState<number[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Load taggable users once
  useEffect(() => {
    if (taskId) {
      api.getTaggableUsers(taskId).then((users: any) => setTaggableUsers(users)).catch(() => {});
    } else if (projectId) {
      api.getProjectTaggableUsers(projectId).then((users: any) => setTaggableUsers(users)).catch(() => {});
    }
  }, [taskId, projectId]);

  // Measure left column to derive collapse height
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const rightCol = el.closest('.initiative-form-split-right');
    if (!rightCol) return;
    const leftCol = rightCol.previousElementSibling as HTMLElement | null;
    if (!leftCol) return;

    const update = () => {
      setCollapseHeight(Math.max(leftCol.offsetHeight, MIN_COLLAPSED_HEIGHT));
    };
    update();

    const observer = new ResizeObserver(update);
    observer.observe(leftCol);
    return () => observer.disconnect();
  }, []);

  // Check for overflow against dynamic collapse height
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    setOverflows(el.scrollHeight > collapseHeight);
  }, [updates, expanded, collapseHeight]);

  const filteredUsers = taggableUsers.filter(u =>
    u.name.toLowerCase().includes(mentionFilter.toLowerCase())
  );

  const handlePost = async (type: 'comment' | 'question' | 'reply') => {
    if (!content.trim() && pendingFiles.length === 0) return;
    setPosting(true);
    try {
      const result = await onPost(content.trim(), type, undefined, taggedUserIds.length > 0 ? taggedUserIds : undefined);
      // If files are pending and we got a comment ID back, upload them before refreshing
      if (pendingFiles.length > 0 && result && projectId) {
        await api.uploadAttachments(projectId, pendingFiles, { task_update_id: result as number });
      }
      onRefresh?.();
      setContent('');
      setTaggedUserIds([]);
      setPendingFiles([]);
    } finally {
      setPosting(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    await api.deleteAttachment(attachmentId);
    onRefresh?.();
  };

  const handleEdit = async () => {
    if (!editingId || !editContent.trim() || !onEdit) return;
    await onEdit(editingId, editContent.trim());
    setEditingId(null);
    setEditContent('');
  };

  const handleDeleteRequest = (updateId: number) => {
    setDeleteConfirm(updateId);
  };

  const handleDeleteConfirm = async () => {
    if (!onDelete || deleteConfirm === null) return;
    await onDelete(deleteConfirm);
    setDeleteConfirm(null);
  };

  const startEdit = (u: TaskUpdate) => {
    setEditingId(u.id);
    setEditContent(u.content);
  };

  // Bottom comment form @mention handlers
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setContent(val);
    const cursorPos = e.target.selectionStart || 0;
    const textBeforeCursor = val.slice(0, cursorPos);
    const lastAtIdx = textBeforeCursor.lastIndexOf('@');

    if (lastAtIdx >= 0) {
      const textAfterAt = textBeforeCursor.slice(lastAtIdx + 1);
      const charBeforeAt = lastAtIdx > 0 ? textBeforeCursor[lastAtIdx - 1] : ' ';
      if (/\s/.test(charBeforeAt) || lastAtIdx === 0) {
        setShowMentionDropdown(true);
        setMentionFilter(textAfterAt);
        setMentionCursorStart(lastAtIdx);
        setSelectedMentionIndex(0);
        return;
      }
    }
    setShowMentionDropdown(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showMentionDropdown || filteredUsers.length === 0) {
      // Enter sends (Shift+Enter for a new line).
      if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault();
        if (!posting && (content.trim() || pendingFiles.length > 0)) handlePost('comment');
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedMentionIndex(i => Math.min(i + 1, filteredUsers.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedMentionIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      const user = filteredUsers[selectedMentionIndex];
      if (user) insertMention(user);
    } else if (e.key === 'Escape') {
      setShowMentionDropdown(false);
    }
  };

  const insertMention = useCallback((user: TaggableUser) => {
    const before = content.slice(0, mentionCursorStart);
    const after = content.slice(textareaRef.current?.selectionStart || content.length);
    const newContent = `${before}@${user.name} ${after}`;
    setContent(newContent);
    setShowMentionDropdown(false);
    if (!taggedUserIds.includes(user.id)) {
      setTaggedUserIds(prev => [...prev, user.id]);
    }
    setTimeout(() => {
      if (textareaRef.current) {
        const pos = before.length + user.name.length + 2;
        textareaRef.current.selectionStart = pos;
        textareaRef.current.selectionEnd = pos;
        textareaRef.current.focus();
      }
    }, 0);
  }, [content, mentionCursorStart, taggedUserIds]);

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) + ' ' +
      d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  const renderContent = (text: string) => {
    const parts = text.split(/(@\w[\w\s]*?\b)/g);
    return parts.map((part, i) => {
      if (part.startsWith('@') && part.length > 1) {
        return <span key={i} className="mention-highlight">{part}</span>;
      }
      return part;
    });
  };

  const tree = buildTree(updates);
  const isCollapsed = !expanded && overflows;

  return (
    <div ref={rootRef}>
      {!hideHeader && <div className="detail-label" style={{ marginBottom: 'var(--s8)' }}>{t('taskTimeline.updates')}</div>}

      {updates.length === 0 && (
        <div style={{ fontSize: 'var(--t-12)', color: 'var(--ink-3)', padding: 'var(--s8) 0' }}>
          {t('taskTimeline.noUpdates')}
        </div>
      )}

      <div
        ref={listRef}
        className={`task-updates-list${isCollapsed ? ' task-updates-list--collapsed' : ''}`}
        style={isCollapsed ? { maxHeight: collapseHeight } : undefined}
      >
        {tree.map((node) => (
          <UpdateNode
            key={node.update.id}
            node={node}
            depth={0}
            readOnly={readOnly}
            currentUserId={currentUserId}
            isAdmin={isAdmin}
            editingId={editingId}
            editContent={editContent}
            replyToId={replyTo?.id ?? null}
            onStartEdit={startEdit}
            onSetEditContent={setEditContent}
            onCancelEdit={() => setEditingId(null)}
            onSaveEdit={handleEdit}
            onDelete={handleDeleteRequest}
            onDeleteAttachment={handleDeleteAttachment}
            onReply={(id, userName) => { setReplyTo({ id, userName }); setExpanded(true); }}
            formatTime={formatTime}
            renderContent={renderContent}
            renderInlineReply={() => (
              <InlineReplyForm
                replyTo={replyTo!}
                taggableUsers={taggableUsers}
                onPost={onPost}
                onCancel={() => setReplyTo(null)}
                projectId={projectId}
                onRefresh={onRefresh}
                allowAttachments={allowAttachments}
              />
            )}
          />
        ))}
      </div>

      {/* Expand chevron */}
      {isCollapsed && (
        <button className="task-updates-expand" onClick={() => setExpanded(true)}>
          <ChevronDown size={16} />
          <span>{t('taskTimeline.updatesCount', { n: updates.length })}</span>
        </button>
      )}

      {/* Bottom comment form (always visible, comment-only — hidden when read-only) */}
      {!readOnly && (
      <div className="task-update-form" style={{ position: 'relative' }}>
        <div style={{ position: 'relative' }}>
          <AutoTextarea
            inputRef={textareaRef}
            value={content}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            placeholder={t('taskTimeline.commentPlaceholder')}
          />
          {showMentionDropdown && filteredUsers.length > 0 && (
            <MentionDropdown users={filteredUsers} selectedIndex={selectedMentionIndex} onSelect={insertMention} />
          )}
        </div>
        {taggedUserIds.length > 0 && (
          <div className="tagged-users-bar">
            <AtSign size={11} />
            {taggableUsers.filter(u => taggedUserIds.includes(u.id)).map(u => (
              <span key={u.id} className="tagged-user-chip">
                {u.name}
                <button className="tagged-user-remove" onClick={() => setTaggedUserIds(prev => prev.filter(id => id !== u.id))}>×</button>
              </span>
            ))}
          </div>
        )}
        <div className="task-update-form-actions">
          {allowAttachments && <FileAttachmentPicker files={pendingFiles} onChange={setPendingFiles} />}
          <span className="task-update-send-hint">{t('taskTimeline.sendHint')}</span>
          <button className="btn btn-accent btn-sm" disabled={posting || (!content.trim() && pendingFiles.length === 0)} onClick={() => handlePost('comment')}>
            <Send size={14} /> {t('common.send')}
          </button>
        </div>
      </div>
      )}

      <ConfirmModal
        open={deleteConfirm !== null}
        title={t('taskTimeline.deleteTitle')}
        message={t('taskTimeline.deleteMessage')}
        confirmLabel={t('common.delete')}
        destructive
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
}
